import './Leads.css';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import api from '../../api/axiosInstance';
import { useModal } from '../../contexts/ModalContext';

const Post = () => {
    const navigate = useNavigate();
    const [setBreadcrumb, setBreadcrumbLink] = useOutletContext();
    const [proceeding, setProceeding] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [tags, setTags] = useState(''); // New state for tags

    const { LeadId } = useParams();
    const { openModal } = useModal();

    useEffect(() => {
        setBreadcrumb(['Marketing', 'Leads', 'Edit']);
        setBreadcrumbLink(['/dashboard', '/dashboard']);
    }, [setBreadcrumb, setBreadcrumbLink]);

    const fetchPosts = async () => {
        try {
            const response = await api.get(`/lead/${LeadId}`);
            if (!response.data.error) {
                const leadData = response.data.data;
                setName(leadData.name);
                setEmail(leadData.email);
                setPhone(leadData.phone);
                // If tags exist (as an array), join them into a comma-separated string for display
                setTags(leadData.tags && Array.isArray(leadData.tags) ? leadData.tags.join(', ') : '');
            } else {
                openModal('failure', response.data.message);
            }
        } catch (error) {
            if (error.message === 'Network Error') {
                openModal('failure', 'No Internet Connection');
            } else {
                openModal('failure', error.message);
            }
        }
    };

    useEffect(() => {
        fetchPosts();
    }, []);

    const handelUpdateLead = async (event) => {
        event.preventDefault();
        try {
            setProceeding(true);
            // Prepare the tags as an array by splitting the comma-separated string
            const tagArray = tags.split(',')
                .map(tag => tag.trim())
                .filter(tag => tag !== '');
            const response = await api.patch(`/lead/${LeadId}`, {
                    name,
                    email,
                    phone,
                    tags: tagArray // Pass the tags array to the update endpoint
                }
            );
            if (!response.error) {
                openModal('success', response.data.message);
                setProceeding(false);
                navigate('/Marketing');
            } else {
                openModal('failure', response.data.message);
                setProceeding(false);
            }
        } catch (error) {
            if (error.message === 'Network Error') {
                openModal('failure', "No Internet Connection.");
            } else {
                openModal('failure', error.message);
            }
        }
    };

    if (!name) return (
        <div className="flex items-center justify-center w-full h-[90vh]">
            <div role="status">
                <svg aria-hidden="true" className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                </svg>
                <span className="sr-only">Loading...</span>
            </div>
        </div>
    );

    return (
        <div className="p-6 bg-white sm:p-8 dark:bg-gray-800 shadow-lg rounded-lg">
            <div className="grid grid-cols-2">
                <div className="col flex items-end">
                    <h3 className="text-xl pb-3 font-bold text-gray-900 dark:text-white">Edit Lead</h3>
                </div>
                <div className="col text-sm mb-2 w-full text-right font-normal text-gray-500 dark:text-gray-400">
                    {/* Additional actions can go here */}
                </div>
            </div>

            <form className="space-y-6 mt-6" onSubmit={handelUpdateLead}>
                <div className="grid grid-cols-6 sm:grid-cols-12 gap-6">
                    <div className="col-span-4">
                        <label htmlFor="name" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Name</label>
                        <input type="text" id="name" onChange={(e) => setName(e.target.value)} value={name} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white" />
                    </div>
                    <div className="col-span-4">
                        <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Email</label>
                        <input type="text" id="email" onChange={(e) => setEmail(e.target.value)} value={email} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white" />
                    </div>
                    <div className="col-span-4">
                        <label htmlFor="phone" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Phone Number</label>
                        <input type="text" id="phone" onChange={(e) => setPhone(e.target.value)} value={phone} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white" />
                    </div>
                    {/* New Tags Input */}
                    <div className="col-span-6">
                        <label htmlFor="tags" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                            Tags (separated by commas)
                        </label>
                        <input 
                            type="text" 
                            id="tags" 
                            onChange={(e) => setTags(e.target.value)} 
                            value={tags} 
                            placeholder="e.g. tag1, tag2" 
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white" 
                        />
                    </div>
                    <div className="col-span-6 sm:col-span-12">
                        <button className="text-white bg-gray-700 hover:bg-gray-800 focus:ring-4 focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-gray-800" type="submit">
                            {proceeding ? (
                                <div className='inline mr-3' role="status">
                                    <svg aria-hidden="true" className="w-5 h-5 inline text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                                        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                                    </svg>
                                    <span className="sr-only">Loading...</span>
                                </div>
                            ) : null}
                            Update Lead
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default Post;